/**
 * EvidenceViewModal Component
 * 
 * Displays evidence (images and delivered quantities) for a completed assignment
 * Prevents re-completion of assignments
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Image as ImageIcon, Package, CheckCircle2, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PackTask, CampaignAtStore } from '@/types/store-first';
import { useAssignmentEvidence } from '@/hooks/useAssignmentEvidence';
import { ModalContextHeader } from '@/components/mobile/ModalContextHeader';

interface EvidenceViewModalProps {
  packTask: PackTask | null;
  campaign: CampaignAtStore;
  isOpen: boolean;
  onClose: () => void;
}

export function EvidenceViewModal({
  packTask,
  campaign,
  isOpen,
  onClose,
}: EvidenceViewModalProps) {
  const isDebug = import.meta.env.DEV;

  const assignmentCandidates = [
    packTask?.assignmentId,
    packTask?.realAssignmentId,
    packTask?.legacyAssignmentId,
  ];

  const {
    data: evidence,
    isLoading,
    error,
    attemptedAssignmentIds,
  } = useAssignmentEvidence(assignmentCandidates);

  // Safety check: Only allow viewing evidence for completed assignments
  const status = packTask?.status as string | undefined;
  const isCompleted = status === 'completed' || status === 'issue' || status === 'operator_complete' || status === 'admin_complete';
  
  // If not completed, close modal and show error
  if (isOpen && !isCompleted) {
    console.warn('[EvidenceViewModal] Attempted to open evidence view for non-completed assignment', {
      assignmentId: packTask?.assignmentId,
      status: packTask?.status
    });
    onClose();
    return null;
  }

  // Find format names from evidence data or packTask formats
  const getFormatName = (formatId: number) => {
    // First try evidence data (has formatName from API)
    const evidenceFormat = evidence?.formatEvidence?.find(f => f.formatId === formatId);
    if (evidenceFormat?.formatName) {
      return evidenceFormat.formatName;
    }
    // Fallback to packTask formats
    const format = packTask?.formats?.find(f => f.formatId === formatId);
    return format?.formatName || `Format ${formatId}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-2 -ml-2 mb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-10 w-10 rounded-lg"
              aria-label="Go back"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
          </div>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            View Evidence
          </DialogTitle>
          <DialogDescription>
            <ModalContextHeader 
              storeName={evidence?.storeName || 'Unknown Store'}
              campaignName={evidence?.campaignName}
              assignmentId={evidence?.resolvedAssignmentId}
            />
            <span className="text-xs text-muted-foreground italic">Read-only view of submitted evidence</span>
            {isDebug && (
              <>
                <br />
                <span className="text-xs text-muted-foreground">
                  Resolved ID: {evidence?.resolvedAssignmentId ?? 'n/a'}
                  {attemptedAssignmentIds?.length
                    ? ` • Attempted IDs: ${attemptedAssignmentIds.join(', ')}`
                    : ''}
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading evidence...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 font-semibold">Failed to load evidence</p>
            <p className="text-red-600 text-sm mt-1">
              {(() => {
                if (error instanceof Error) {
                  const attempted = isDebug && attemptedAssignmentIds?.length
                    ? ` (attempted IDs: ${attemptedAssignmentIds.join(', ')})`
                    : '';
                  return `${error.message}${attempted}`;
                }
                return 'Unknown error occurred';
              })()}
            </p>
          </div>
        )}

        {evidence && !isLoading && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-blue-900">Delivery Summary</h3>
                <Badge className="bg-green-100 text-green-700">
                  Completed
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <p className="text-sm text-gray-600">Planned Quantity</p>
                  <p className="text-lg font-semibold">{evidence.assignment.plannedQuantity}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Actual Quantity</p>
                  <p className={cn(
                    "text-lg font-semibold",
                    evidence.assignment.deliveredQuantity === evidence.assignment.plannedQuantity
                      ? "text-green-700"
                      : "text-orange-700"
                  )}>
                    {evidence.assignment.deliveredQuantity}
                  </p>
                </div>
              </div>
              {evidence.notes && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-sm text-gray-600">Notes</p>
                  <p className="text-sm text-gray-800 mt-1">{evidence.notes}</p>
                </div>
              )}
            </div>

            {/* Format-level Evidence - ONLY show if we have actual committed data */}
            {evidence.formatEvidence.length > 0 && (
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Format Details (Submitted)
                </h3>
                <div className="space-y-2">
                  {evidence.formatEvidence.map((fe) => (
                    <div
                      key={fe.formatId}
                      className="p-3 rounded-lg border bg-gray-50"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{getFormatName(fe.formatId)}</span>
                        <Badge
                          variant={fe.actualCount === fe.expectedCount ? "default" : "secondary"}
                          className={cn(
                            fe.actualCount === fe.expectedCount
                              ? "bg-green-100 text-green-700"
                              : "bg-orange-100 text-orange-700"
                          )}
                        >
                          {fe.actualCount} / {fe.expectedCount}
                        </Badge>
                      </div>
                      {fe.discrepancyReason && (
                        <p className="text-xs text-gray-600 mt-1">
                          Reason: {fe.discrepancyReason}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Proof Images */}
            {evidence.proofImages.length > 0 ? (
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  Proof Images ({evidence.proofImages.length})
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {evidence.proofImages.map((img) => (
                    <div key={img.id} className="relative group">
                      <img
                        src={img.url}
                        alt={`Proof image ${img.id}`}
                        className="w-full h-48 object-cover rounded-lg border border-gray-200"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded-lg" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No proof images available</p>
              </div>
            )}

            {/* Manager Signature - if available */}
            {evidence.signatureUrl && (
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Manager Signature
                </h3>
                <div className="flex justify-center">
                  <img
                    src={evidence.signatureUrl}
                    alt="Manager signature"
                    className="max-h-48 border-2 border-green-200 rounded-lg bg-white p-2"
                    loading="lazy"
                  />
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Bottom safe area spacer for Android navigation bar */}
        <div className="h-[max(env(safe-area-inset-bottom,0px),80px)] min-h-[80px]" />
      </DialogContent>
    </Dialog>
  );
}

